import localforage from "localforage";
import { useCallback, useEffect, useState } from "react";

import logError from "seneca-common/utils/sentry/logError";

async function getValue<T>(key: string, errorValue?: T) {
  try {
    await localforage.ready();
    return await localforage.getItem<T>(key);
  } catch (e: any) {
    logError(e);
    return errorValue;
  }
}

async function storeValue<T>(key: string, value: T) {
  try {
    await localforage.ready();
    await localforage.setItem(key, value);
  } catch (err: any) {
    logError(err);
  }
}

export default function useLocalForage<T>({
  key,
  initialValue,
  errorValue
}: {
  key: string;
  initialValue?: T;
  errorValue?: T;
}) {
  const [value, setValue] = useState<T | undefined | null>(initialValue);

  const _getValue = useCallback(async () => {
    const value = await getValue(key, errorValue);
    setValue(value);
    return value;
  }, [key, errorValue]);

  const _storeValue = useCallback(
    async (value: T) => {
      await storeValue(key, value);
      setValue(value);
    },
    [key]
  );

  useEffect(() => {
    (async () => {
      await _getValue();
    })();
  }, [_getValue]);

  return {
    value,
    get: _getValue,
    set: _storeValue
  };
}
